import { useMemo } from "react";

/**
 * RoundTable — visual circular table with seats around the circumference.
 * Props:
 *   table: { id, name, seats, assignments }
 *   size: pixel size of outer container (default 240)
 *   onSeatClick(index): optional — admin click handler for a seat
 *   highlightEmpty: bool — show empty seats with dashed border (admin)
 */
export const RoundTable = ({
  table,
  size = 240,
  onSeatClick,
  highlightEmpty = false,
}) => {
  const { name, seats, assignments = [] } = table;
  const radius = size / 2;
  // Seat positions placed AROUND the table: a bit outside the circle edge
  const seatRadius = radius - 14;
  const seatSize = Math.max(28, Math.min(40, size / 7));

  const positions = useMemo(() => {
    if (!seats || seats <= 0) return [];
    return Array.from({ length: seats }, (_, i) => {
      const angle = (2 * Math.PI * i) / seats - Math.PI / 2;
      return {
        x: radius + Math.cos(angle) * seatRadius,
        y: radius + Math.sin(angle) * seatRadius,
      };
    });
  }, [seats, radius, seatRadius]);

  return (
    <div
      className="relative shrink-0"
      style={{ width: size, height: size }}
      data-testid={`round-table-${table.id}`}
    >
      {/* Round table body */}
      <div
        className="absolute inset-6 rounded-full bg-gradient-to-br from-sage-100 to-sage-200 border border-sage-300 flex items-center justify-center shadow-inner"
      >
        <span className="font-serif text-sage-700 text-base md:text-lg text-center px-3">
          {name}
        </span>
      </div>

      {/* Seats placed around the table */}
      {positions.map((p, i) => {
        const guest = assignments[i] || "";
        const filled = !!guest;
        const clickable = !!onSeatClick;
        return (
          <button
            key={i}
            type="button"
            data-testid={`seat-${table.id}-${i}`}
            onClick={clickable ? () => onSeatClick(i) : undefined}
            disabled={!clickable}
            title={guest || `Platz ${i + 1}`}
            className={`absolute rounded-full text-[10px] font-medium flex items-center justify-center text-center transition-all ${
              filled
                ? "bg-sage-500 text-white border-2 border-sage-600"
                : highlightEmpty
                ? "bg-white border-2 border-dashed border-sage-300 text-sage-400 hover:border-sage-500"
                : "bg-white border-2 border-sage-300 text-sage-400"
            } ${clickable ? "cursor-pointer hover:scale-110" : "cursor-default"}`}
            style={{
              width: seatSize,
              height: seatSize,
              left: p.x - seatSize / 2,
              top: p.y - seatSize / 2,
              padding: 2,
              lineHeight: 1.05,
            }}
          >
            {filled ? abbreviate(guest) : i + 1}
          </button>
        );
      })}
    </div>
  );
};

const abbreviate = (full) => {
  if (!full) return "";
  const parts = full.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 6);
  return `${parts[0][0]}.${parts[parts.length - 1].slice(0, 6)}`;
};

export default RoundTable;
